import {
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core'
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew'

import { formatSessionTime } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { Session } from '../../types/iamSessions'
import SessionStateBox from '../SessionStateBox'
import { useStyles } from './styles/index.styles'

const TERMINABLE_STATES = ['Active', 'Pending2FA', 'PendingUpdatePhoneNumber']

type SessionRowProps = {
  session: Session
  hideEmail?: boolean
  selectedSessionsIds?: { uuid: string; id: string }[]
  setSelectedSessionsIds?: React.Dispatch<
    React.SetStateAction<{ uuid: string; id: string }[]>
  >
  // Renders a per-session terminate action in its own Actions column -- only in the
  // full (non-hideEmail) layout. hideEmail consumers (the Home dashboard's read-only
  // session widget) render neither the checkbox column nor this one; its header has
  // no "Actions" label to match either (BL-235's original defect: a bulk-only action
  // column grew the AdminConfigSessions row's grid with nothing in it; the same
  // shape existed independently on the Home widget as a dead "Actions" header label
  // with no column under it at all -- fixed by removing the label, not adding a cell,
  // since no terminate mutation is wired into that read-only view).
  onTerminate?: (session: Session) => void
  terminatingSessionId?: string
}
export default function SessionRow({
  session,
  hideEmail,
  selectedSessionsIds = [],
  setSelectedSessionsIds,
  onTerminate,
  terminatingSessionId
}: SessionRowProps) {
  const classes = useStyles()
  const isSelected = selectedSessionsIds.some(
    (item) => item.uuid === session.sessionUuid
  )
  const canTerminate = TERMINABLE_STATES.includes(session.sessionState)
  const isTerminating = terminatingSessionId === session.id
  const handleToggle = () => {
    setSelectedSessionsIds?.((prev) => {
      if (isSelected) {
        // remove
        return prev.filter((item) => item.uuid !== session.sessionUuid)
      } else {
        // add
        return [
          ...prev,
          {
            uuid: session.sessionUuid,
            id: session.id // ← assuming Session has .id field
          }
        ]
      }
    })
  }
  return (
    <div
      className={[classes.tableRow, hideEmail ? classes.hideEmailRow : ''].join(
        ' '
      )}>
      {!hideEmail && (
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={handleToggle}
          disabled={!canTerminate}
        />
      )}
      <Typography className={classes.text}>
        {session.sessionUuid.length < 15
          ? session.sessionUuid
          : `${session.sessionUuid.substr(0, 15)} ...`}
      </Typography>
      {!hideEmail && (
        <Typography className={classes.text}>
          {session.email.length < 30
            ? session.email
            : session.email.substr(0, 30)}
        </Typography>
      )}
      <SessionStateBox state={session.sessionState} />
      <Typography className={classes.text}>
        {formatSessionTime(session.initiatedAt)}
      </Typography>
      <Typography className={classes.text}>
        {formatSessionTime(session.lastActivity)}
      </Typography>
      <Typography className={classes.text}>
        {formatSessionTime(session.terminatedAt)}
      </Typography>
      <Typography className={classes.text}>
        {formatSessionTime(session.accessTokenExpiry)}
      </Typography>
      <Typography className={classes.text}>
        {formatSessionTime(session.absoluteExpiry)}
      </Typography>
      {!hideEmail && onTerminate && (
        <Tooltip title={getTranslate('Terminate')}>
          <span>
            <IconButton
              size="small"
              aria-label={getTranslate('Terminate')}
              disabled={!canTerminate || isTerminating}
              onClick={() => onTerminate(session)}>
              {isTerminating ? (
                <CircularProgress size={16} />
              ) : (
                <PowerSettingsNewIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  )
}
